import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { Mail, MapPin, Send, CheckCircle } from 'lucide-react'
import { Section, Card, Button, Input, Textarea } from '../components/ui'
import { AREAS_OF_INTEREST } from '../constants'
import { cn } from '../utils'

const contactInfo = [
  {
    icon: Mail,
    label: 'Email',
    value: 'info@matno.org',
    href: 'mailto:info@matno.org',
  },
  {
    icon: MapPin,
    label: 'Location',
    value: 'Africa',
    href: null,
  },
]

type FormData = {
  name: string
  organization: string
  email: string
  areaOfInterest: string
  message: string
}

const initialForm: FormData = {
  name: '',
  organization: '',
  email: '',
  areaOfInterest: '',
  message: '',
}

const Contact: React.FC = () => {
  const location = useLocation()
  const [formData, setFormData] = useState<FormData>(initialForm)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Scroll to hash target (e.g. /contact#partner)
  useEffect(() => {
    if (!location.hash) return
    const id = location.hash.slice(1)
    const t = window.setTimeout(() => {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
    return () => window.clearTimeout(t)
  }, [location.hash])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.email.trim()) newErrors.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Please enter a valid email'
    if (!formData.message.trim()) newErrors.message = 'Message is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    setIsSubmitting(true)
    await new Promise(r => setTimeout(r, 1200))
    console.info('[MATNO contact]', formData)
    setIsSubmitting(false)
    setIsSubmitted(true)
    setFormData(initialForm)
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  return (
    <>
      {/* Hero */}
      <Section background="muted" padding="xl" className="pt-32 md:pt-40">
        <motion.div
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-medium tracking-[0.22em] uppercase text-primary-600 dark:text-primary-300 mb-4">
            Partner &amp; Contact
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 dark:text-white mb-6 leading-tight">
            Let's build from{' '}
            <span className="text-secondary-500 dark:text-secondary-300">insight</span> to{' '}
            <span className="text-primary-500 dark:text-primary-300">impact</span>.
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
            MATNO is open to partnerships with institutions, researchers, innovators,
            funders, and practitioners working to advance responsible digital
            transformation in Africa.
          </p>
        </motion.div>
      </Section>

      {/* Form + info */}
      <Section id="partner" background="default" padding="lg">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          {/* Left column: info */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Get in touch
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              We typically respond within 2–3 business days.
            </p>

            <div className="space-y-4">
              {contactInfo.map(item => {
                const Icon = item.icon
                const content = (
                  <div className="flex items-start gap-4">
                    <div
                      className={cn(
                        'w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center',
                        'bg-secondary-50 dark:bg-secondary-900/40',
                        'text-secondary-500 dark:text-secondary-300'
                      )}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                        {item.label}
                      </p>
                      <p className="text-gray-900 dark:text-white font-medium">
                        {item.value}
                      </p>
                    </div>
                  </div>
                )
                return item.href ? (
                  <a
                    key={item.label}
                    href={item.href}
                    className="block hover:opacity-80 transition-opacity"
                  >
                    {content}
                  </a>
                ) : (
                  <div key={item.label}>{content}</div>
                )
              })}
            </div>

            <div
              className={cn(
                'mt-10 p-5 rounded-xl',
                'bg-primary-50/60 dark:bg-primary-900/25',
                'border border-primary-100 dark:border-primary-800/60'
              )}
            >
              <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                <span className="font-medium text-gray-900 dark:text-white">
                  Institutional partnerships
                </span>
                <br />
                Governments, universities, development partners and funders can also
                reach us at{' '}
                <a
                  href="mailto:partnerships@matno.org"
                  className="text-primary-600 dark:text-primary-300 font-medium hover:underline"
                >
                  partnerships@matno.org
                </a>
                .
              </p>
            </div>
          </motion.div>

          {/* Right column: form */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Card variant="default" padding="lg">
              {isSubmitted ? (
                <motion.div
                  className="text-center py-10"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div
                    className={cn(
                      'w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4',
                      'bg-primary-50 dark:bg-primary-900/40',
                      'text-primary-600 dark:text-primary-300'
                    )}
                  >
                    <CheckCircle className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Thank you — message received.
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
                    A member of the MATNO team will reach out shortly.
                  </p>
                  <Button variant="outline" onClick={() => setIsSubmitted(false)}>
                    Send another message
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Input
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      error={errors.name}
                      placeholder="Your name"
                      required
                    />
                    <Input
                      label="Organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      placeholder="Institution, company, or independent"
                    />
                  </div>

                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                    placeholder="you@example.com"
                    required
                  />

                  <div className="space-y-1">
                    <label
                      htmlFor="areaOfInterest"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                    >
                      Area of interest
                    </label>
                    <select
                      id="areaOfInterest"
                      name="areaOfInterest"
                      value={formData.areaOfInterest}
                      onChange={handleChange}
                      className={cn(
                        'w-full px-4 py-2.5 rounded-lg',
                        'bg-white dark:bg-gray-900',
                        'border border-gray-200 dark:border-gray-700',
                        'text-gray-900 dark:text-gray-100',
                        'transition-all duration-200',
                        'focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400'
                      )}
                    >
                      <option value="">Select an area…</option>
                      {AREAS_OF_INTEREST.map(area => (
                        <option key={area} value={area}>
                          {area}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Textarea
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    error={errors.message}
                    placeholder="Tell us how you'd like to collaborate…"
                    rows={5}
                    required
                  />

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      variant="primary"
                      loading={isSubmitting}
                      icon={<Send className="w-4 h-4" />}
                    >
                      {isSubmitting ? 'Sending…' : 'Send message'}
                    </Button>
                  </div>
                </form>
              )}
            </Card>
          </motion.div>
        </div>
      </Section>
    </>
  )
}

export default Contact
